import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import {
  Form,
  Button,
  Row,
  Col,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Pagination,
} from 'react-bootstrap';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import {
  FaSearch,
  FaFileExcel,
  FaExclamationCircle,
  FaCalendarAlt,
  FaEraser,
} from 'react-icons/fa';
import { Formik } from 'formik';
import Selector from 'react-select';
import MaskedInput from 'react-text-mask';
import { toast } from 'react-toastify';
import moment from 'moment';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import 'bootstrap/dist/css/bootstrap.min.css';

import Header from '~/Components/Header/Header';
import DateMask from '~/Components/DateMask';
import * as constants from '~/constant';
import { formatarMonetario } from '~/Utils/Helpers';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  getEmpreendimentosVendasDesconto,
  buscarVendasDesconto,
  exportarVendasDesconto,
} from '~/Services/Service';

const REGISTROS_POR_PAGINA = 20;

const baixarBlob = (blob, nomeArquivo) => {
  const url = window.URL.createObjectURL(
    new Blob([blob], {
      type:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeArquivo;
  link.click();
  window.URL.revokeObjectURL(url);
  link.remove();
};

export const RelatorioVendasDesconto = () => {
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [carregandoEmpreendimentos, setCarregandoEmpreendimentos] = useState(
    true
  );
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [openDatePickerInicial, setOpenDatePickerInicial] = useState(false);
  const [openDatePickerFinal, setOpenDatePickerFinal] = useState(false);
  const [filtroAplicado, setFiltroAplicado] = useState(null);

  // LICOES Ciclo 9 L3 — anti-race em useEffect
  useEffect(() => {
    let ativo = true;
    setCarregandoEmpreendimentos(true);
    getEmpreendimentosVendasDesconto()
      .then(res => {
        if (!ativo) return;
        const opcoes = (res || []).map(item => ({
          value: item.codigo_familia,
          label: `${item.codigo_familia} - ${item.nome_da_familia}`,
        }));
        setEmpreendimentos(opcoes);
      })
      .catch(err => {
        if (!ativo) return;
        const msg =
          err && err.message
            ? err.message
            : 'Erro ao carregar empreendimentos.';
        toast.error(msg);
      })
      .finally(() => {
        if (!ativo) return;
        setCarregandoEmpreendimentos(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        dataInicio: Yup.string()
          .required('Data inicial é obrigatória')
          .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato inválido (DD/MM/YYYY)')
          .test('valid-date', 'Data inválida', value =>
            value ? moment(value, 'DD/MM/YYYY', true).isValid() : true
          ),
        dataFim: Yup.string()
          .required('Data final é obrigatória')
          .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato inválido (DD/MM/YYYY)')
          .test('valid-date', 'Data inválida', value =>
            value ? moment(value, 'DD/MM/YYYY', true).isValid() : true
          )
          .test(
            'data-fim-valida',
            'Data final deve ser maior ou igual à data inicial',
            function(value) {
              const { dataInicio } = this.parent;
              if (!dataInicio || !value) return true;
              return moment(value, 'DD/MM/YYYY').isSameOrAfter(
                moment(dataInicio, 'DD/MM/YYYY')
              );
            }
          ),
        codigosFamilia: Yup.array()
          .min(1, 'Selecione ao menos um empreendimento')
          .required('Selecione ao menos um empreendimento'),
      }),
    []
  );

  const montarFiltro = values => ({
    dataInicio: moment(values.dataInicio, 'DD/MM/YYYY').format('YYYY-MM-DD'),
    dataFim: moment(values.dataFim, 'DD/MM/YYYY').format('YYYY-MM-DD'),
    codigosFamilia: values.codigosFamilia.map(item => item.value).join(','),
  });

  const handleBuscar = async (values, { setSubmitting }) => {
    setLoading(true);
    setResultado(null);
    setPaginaAtual(1);
    const filtro = montarFiltro(values);
    try {
      const res = await buscarVendasDesconto(filtro);
      setResultado(res || { total: 0, registros: [] });
      setFiltroAplicado(filtro);
    } catch (err) {
      const msg =
        err && err.message ? err.message : 'Erro ao buscar relatório.';
      toast.error(msg);
      setFiltroAplicado(null);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleExportar = async () => {
    if (!filtroAplicado) return;
    setExportando(true);
    try {
      const blob = await exportarVendasDesconto(filtroAplicado);
      const nomeArquivo = `vendas-com-desconto-${filtroAplicado.dataInicio}-${filtroAplicado.dataFim}.xlsx`;
      baixarBlob(blob, nomeArquivo);
      toast.success('Relatório exportado com sucesso!');
    } catch (err) {
      const msg =
        err && err.message ? err.message : 'Erro ao exportar relatório.';
      toast.error(msg);
    } finally {
      setExportando(false);
    }
  };

  const registros = resultado ? resultado.registros || [] : [];
  const totalPaginas = Math.max(
    1,
    Math.ceil(registros.length / REGISTROS_POR_PAGINA)
  );
  const registrosPagina = useMemo(() => {
    const inicio = (paginaAtual - 1) * REGISTROS_POR_PAGINA;
    return registros.slice(inicio, inicio + REGISTROS_POR_PAGINA);
  }, [registros, paginaAtual]);

  const renderizarPaginacao = () => {
    if (totalPaginas <= 1) return null;

    const itens = [];
    const maxNumeros = 5;
    let inicio = Math.max(1, paginaAtual - Math.floor(maxNumeros / 2));
    let fim = Math.min(totalPaginas, inicio + maxNumeros - 1);
    if (fim - inicio + 1 < maxNumeros) {
      inicio = Math.max(1, fim - maxNumeros + 1);
    }

    for (let n = inicio; n <= fim; n += 1) {
      itens.push(
        <Pagination.Item
          key={n}
          active={n === paginaAtual}
          onClick={() => setPaginaAtual(n)}
        >
          {n}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="mb-0">
        <Pagination.First
          onClick={() => setPaginaAtual(1)}
          disabled={paginaAtual === 1}
        />
        <Pagination.Prev
          onClick={() => setPaginaAtual(paginaAtual - 1)}
          disabled={paginaAtual === 1}
        />
        {inicio > 1 && <Pagination.Ellipsis disabled />}
        {itens}
        {fim < totalPaginas && <Pagination.Ellipsis disabled />}
        <Pagination.Next
          onClick={() => setPaginaAtual(paginaAtual + 1)}
          disabled={paginaAtual === totalPaginas}
        />
        <Pagination.Last
          onClick={() => setPaginaAtual(totalPaginas)}
          disabled={paginaAtual === totalPaginas}
        />
      </Pagination>
    );
  };

  return (
    <div>
      <Header title="Relatório de Vendas com Desconto" />
      <Formik
        initialValues={{
          dataInicio: moment()
            .startOf('month')
            .format('DD/MM/YYYY'),
          dataFim: moment().format('DD/MM/YYYY'),
          codigosFamilia: [],
        }}
        validationSchema={validationSchema}
        onSubmit={handleBuscar}
      >
        {({
          handleSubmit,
          handleReset,
          setFieldValue,
          setFieldTouched,
          errors,
          touched,
          values,
          isSubmitting,
        }) => {
          const selecionarTodos = () => {
            setFieldValue('codigosFamilia', empreendimentos);
            setFieldTouched('codigosFamilia', true);
          };

          const limpar = () => {
            handleReset();
            setResultado(null);
            setFiltroAplicado(null);
            setPaginaAtual(1);
          };

          return (
            <Form
              onSubmit={handleSubmit}
              className="container-fluid bg-white p-3"
            >
              <Row>
                <Col sm={12} md={2}>
                  <Form.Group>
                    <Form.Label>Data Inicial</Form.Label>
                    <DateMask allowEmpty>
                      {() => (
                        <div className="position-relative">
                          <MaskedInput
                            name="dataInicio"
                            type="tel"
                            guide={false}
                            className={`form-control ${
                              errors.dataInicio && touched.dataInicio
                                ? 'is-invalid'
                                : ''
                            }`}
                            placeholder="Ex.: 01/01/2026"
                            mask={constants.MASK_DATE}
                            value={values.dataInicio}
                            onChange={e => {
                              setFieldValue('dataInicio', e.target.value);
                            }}
                            onBlur={() => {
                              setFieldTouched('dataInicio', true);
                            }}
                            style={{ paddingRight: '65px' }}
                          />
                          <div
                            className="position-absolute d-flex align-items-center"
                            style={{
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              gap: '5px',
                            }}
                          >
                            {errors.dataInicio && touched.dataInicio && (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="tooltip-data-inicio">
                                    {errors.dataInicio}
                                  </Tooltip>
                                }
                              >
                                <FaExclamationCircle
                                  className="text-danger"
                                  style={{ cursor: 'pointer' }}
                                />
                              </OverlayTrigger>
                            )}
                            <FaCalendarAlt
                              className="text-secondary"
                              style={{ cursor: 'pointer' }}
                              onClick={() => setOpenDatePickerInicial(true)}
                            />
                          </div>
                          <MuiPickersUtilsProvider
                            utils={MomentUtils}
                            locale="pt-br"
                          >
                            <DatePicker
                              open={openDatePickerInicial}
                              onOpen={() => setOpenDatePickerInicial(true)}
                              onClose={() => setOpenDatePickerInicial(false)}
                              value={
                                values.dataInicio
                                  ? moment(values.dataInicio, 'DD/MM/YYYY')
                                  : null
                              }
                              onChange={date => {
                                setFieldValue(
                                  'dataInicio',
                                  date ? date.format('DD/MM/YYYY') : ''
                                );
                                setOpenDatePickerInicial(false);
                              }}
                              format="DD/MM/YYYY"
                              TextFieldComponent={() => null}
                            />
                          </MuiPickersUtilsProvider>
                        </div>
                      )}
                    </DateMask>
                  </Form.Group>
                </Col>
                <Col sm={12} md={2}>
                  <Form.Group>
                    <Form.Label>Data Final</Form.Label>
                    <DateMask allowEmpty>
                      {() => (
                        <div className="position-relative">
                          <MaskedInput
                            name="dataFim"
                            type="tel"
                            guide={false}
                            className={`form-control ${
                              errors.dataFim && touched.dataFim
                                ? 'is-invalid'
                                : ''
                            }`}
                            placeholder="Ex.: 31/01/2026"
                            mask={constants.MASK_DATE}
                            value={values.dataFim}
                            onChange={e => {
                              setFieldValue('dataFim', e.target.value);
                            }}
                            onBlur={() => {
                              setFieldTouched('dataFim', true);
                            }}
                            style={{ paddingRight: '65px' }}
                          />
                          <div
                            className="position-absolute d-flex align-items-center"
                            style={{
                              right: '10px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              gap: '5px',
                            }}
                          >
                            {errors.dataFim && touched.dataFim && (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="tooltip-data-fim">
                                    {errors.dataFim}
                                  </Tooltip>
                                }
                              >
                                <FaExclamationCircle
                                  className="text-danger"
                                  style={{ cursor: 'pointer' }}
                                />
                              </OverlayTrigger>
                            )}
                            <FaCalendarAlt
                              className="text-secondary"
                              style={{ cursor: 'pointer' }}
                              onClick={() => setOpenDatePickerFinal(true)}
                            />
                          </div>
                          <MuiPickersUtilsProvider
                            utils={MomentUtils}
                            locale="pt-br"
                          >
                            <DatePicker
                              open={openDatePickerFinal}
                              onOpen={() => setOpenDatePickerFinal(true)}
                              onClose={() => setOpenDatePickerFinal(false)}
                              value={
                                values.dataFim
                                  ? moment(values.dataFim, 'DD/MM/YYYY')
                                  : null
                              }
                              onChange={date => {
                                setFieldValue(
                                  'dataFim',
                                  date ? date.format('DD/MM/YYYY') : ''
                                );
                                setOpenDatePickerFinal(false);
                              }}
                              format="DD/MM/YYYY"
                              TextFieldComponent={() => null}
                            />
                          </MuiPickersUtilsProvider>
                        </div>
                      )}
                    </DateMask>
                  </Form.Group>
                </Col>
                <Col sm={12} md={6}>
                  <Form.Group>
                    <Form.Label>Empreendimentos</Form.Label>
                    <Selector
                      isMulti
                      isLoading={carregandoEmpreendimentos}
                      isDisabled={carregandoEmpreendimentos}
                      options={empreendimentos}
                      value={values.codigosFamilia}
                      onChange={selecionados => {
                        setFieldValue('codigosFamilia', selecionados || []);
                        setFieldTouched('codigosFamilia', true);
                      }}
                      onBlur={() => setFieldTouched('codigosFamilia', true)}
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      placeholder="Selecione um ou mais empreendimentos..."
                      noOptionsMessage={() => 'Nenhum empreendimento encontrado'}
                      closeMenuOnSelect={false}
                      getOptionValue={opt => opt.value}
                      getOptionLabel={opt => opt.label}
                    />
                    {errors.codigosFamilia && touched.codigosFamilia && (
                      <small className="text-danger d-block mt-1">
                        {typeof errors.codigosFamilia === 'string'
                          ? errors.codigosFamilia
                          : 'Selecione ao menos um empreendimento'}
                      </small>
                    )}
                  </Form.Group>
                </Col>
                <Col sm={12} md={2} className="d-flex align-items-end">
                  <Form.Group className="w-100">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      className="w-100"
                      onClick={selecionarTodos}
                      disabled={
                        carregandoEmpreendimentos ||
                        empreendimentos.length === 0
                      }
                    >
                      Selecionar todos
                    </Button>
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex justify-content-end align-items-center">
                <Button
                  type="button"
                  variant="outline-secondary"
                  className="mr-2"
                  onClick={limpar}
                  disabled={loading || isSubmitting}
                >
                  <FaEraser className="mr-2" />
                  Limpar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || isSubmitting}
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="mr-2"
                      />
                      Buscando...
                    </>
                  ) : (
                    <>
                      <FaSearch className="mr-2" />
                      Gerar Relatório
                    </>
                  )}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>

      {resultado && registros.length > 0 && (
        <>
          <div className="container-fluid bg-white p-3 mt-3">
            <Row className="align-items-center">
              <Col>
                <span>
                  Total de {resultado.total || registros.length} registro(s)
                  encontrado(s).
                </span>
              </Col>
              <Col xs="auto">
                <Button
                  variant="success"
                  onClick={handleExportar}
                  disabled={exportando || loading}
                >
                  {exportando ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="mr-2"
                      />
                      Exportando...
                    </>
                  ) : (
                    <>
                      <FaFileExcel className="mr-2" />
                      Exportar Excel
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </div>

          <div className="container-fluid bg-white table-responsive">
            <Table className="table table-striped table-bordered table-hover">
              <Thead>
                <Tr className="text-center">
                  <Th>Pré-Venda</Th>
                  <Th>VV Tabela</Th>
                  <Th>Diferença</Th>
                  <Th>Sinal Dentro</Th>
                  <Th>Qtd Sinal</Th>
                  <Th>Qtd Financ.</Th>
                </Tr>
              </Thead>
              <Tbody className="text-center">
                {registrosPagina.map(item => (
                  <Tr key={item.prevenda}>
                    <Td>{item.prevenda}</Td>
                    <Td className="text-right">
                      {formatarMonetario(item.vv_tabela)}
                    </Td>
                    <Td className="text-right">
                      {formatarMonetario(item.diferenca)}
                    </Td>
                    <Td className="text-right">
                      {formatarMonetario(item.sinal_dentro)}
                    </Td>
                    <Td>{item.qtd_sinal_dentro}</Td>
                    <Td>{item.qtd_financ}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>

          <div className="container-fluid bg-white p-3">
            <Row className="d-flex justify-content-between align-items-center">
              <Col xs="auto">
                <span>
                  Mostrando{' '}
                  {Math.min(
                    (paginaAtual - 1) * REGISTROS_POR_PAGINA + 1,
                    registros.length
                  )}{' '}
                  até{' '}
                  {Math.min(
                    paginaAtual * REGISTROS_POR_PAGINA,
                    registros.length
                  )}{' '}
                  de {registros.length} registros
                </span>
              </Col>
              <Col xs="auto">{renderizarPaginacao()}</Col>
            </Row>
          </div>
        </>
      )}

      {resultado && registros.length === 0 && !loading && (
        <div className="container-fluid bg-white p-5 mt-3 text-center">
          <Row>
            <Col>
              <FaExclamationCircle size={48} className="text-muted mb-3" />
              <h5 className="text-muted">
                Nenhuma venda com desconto encontrada para os filtros
                selecionados.
              </h5>
            </Col>
          </Row>
        </div>
      )}

      {!resultado && !loading && (
        <div className="container-fluid bg-white p-5 mt-3 text-center">
          <Row>
            <Col>
              <FaExclamationCircle size={48} className="text-muted mb-3" />
              <h5 className="text-muted">
                Utilize os filtros acima para gerar o relatório.
              </h5>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default RelatorioVendasDesconto;
